/**
 * Raffle Tickets — the Gemstone raffle ledger for the Shuswap Hospice "Dancing
 * with the Shuswap Stars" 2026 fundraiser (admin).
 *
 * Everything here goes through /api/raffle (api/raffle.js); the page decides
 * nothing about numbering or verification. What it is for:
 *   • draw day: type a ticket number, see who holds it
 *   • the running count: sold / left / verified / pending / void
 *   • Verify (an e-transfer that arrived some other way) and Void (never paid)
 *   • the CSV the hospice needs (name, phone, e-mail per ticket)
 *
 * The PUBLIC form is /raffle (a Vercel rewrite to the same endpoint), which is
 * why this page lives at /raffle/admin.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/raffle";

const C = {
  bg: "#0d0f12", surface: "#161a20", surface2: "#1e2328", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,.12)", text: "#e8eaf0",
  muted: "#8892a4", red: "#ef4444", redDim: "rgba(239,68,68,.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,.12)", yellow: "#eab308", yellowDim: "rgba(234,179,8,.12)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,.12)",
};
const STATUS = {
  pending:  { label: "Pending e-transfer", color: C.yellow, bg: C.yellowDim },
  verified: { label: "Verified",          color: C.green,  bg: C.greenDim },
  void:     { label: "Void",              color: C.muted,  bg: "rgba(136,146,164,.12)" },
};
const STATE_LABEL = {
  off: ["Switched OFF", C.red], before: ["Opens Oct 20", C.yellow], open: ["OPEN — selling", C.green], ended: ["Sales ended", C.muted],
};

const money = (cents) => `$${(Number(cents || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const rangeText = (nums) => {
  const n = [...(nums || [])].sort((a, b) => a - b);
  if (!n.length) return "";
  if (n.length === 1) return String(n[0]);
  const consecutive = n.every((v, i) => i === 0 || v === n[i - 1] + 1);
  if (n.length === 2) return `${n[0]} & ${n[1]}`;
  return consecutive ? `${n[0]}–${n[n.length - 1]}` : n.join(", ");
};

function Btn({ children, onClick, disabled, kind = "ghost", size = "md", title }) {
  const styles = {
    ghost: { background: "transparent", color: C.text, border: `1px solid ${C.border}` },
    accent: { background: C.accent, color: "#0d0f12", border: `1px solid ${C.accent}`, fontWeight: 700 },
    green: { background: C.greenDim, color: C.green, border: `1px solid ${C.green}` },
    danger: { background: C.redDim, color: C.red, border: "1px solid rgba(239,68,68,.4)" },
  }[kind];
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled} style={{
      ...styles, padding: size === "sm" ? "5px 10px" : "8px 14px", borderRadius: 8,
      fontSize: size === "sm" ? 12 : 13, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
    }}>{children}</button>
  );
}
function Badge({ label, color, bg }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 700, color, background: bg, whiteSpace: "nowrap" }}>{label}</span>;
}
function Tile({ label, value, sub, color }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", minWidth: 120, flex: "1 1 120px" }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || C.text, lineHeight: 1.2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: C.muted }}>{sub}</div> : null}
    </div>
  );
}

function ClaimCard({ c, busy, onVerify, onVoid }) {
  const s = STATUS[c.status] || STATUS.pending;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10, opacity: c.status === "void" ? 0.6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: ".03em", color: C.text }}>{rangeText(c.tickets)}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Badge {...s} />
          <span style={{ fontSize: 13, color: C.muted }}>{c.tickets.length} ticket{c.tickets.length === 1 ? "" : "s"} · {money(c.amount_cents)}{Number(c.donation_cents) > 0 ? ` (incl. ${money(c.donation_cents)} donation)` : ""}</span>
        </div>
      </div>
      <div style={{ marginTop: 6, fontSize: 14, color: C.text }}>
        <strong>{c.buyer_name}</strong>
        {c.buyer_phone ? <> · <a href={`tel:${String(c.buyer_phone).replace(/\D/g, "")}`} style={{ color: C.blue }}>{c.buyer_phone}</a></> : null}
        {c.buyer_email ? <> · <a href={`mailto:${c.buyer_email}`} style={{ color: C.blue }}>{c.buyer_email}</a></> : null}
      </div>
      <div style={{ marginTop: 2, fontSize: 13, color: C.muted }}>
        {[c.buyer_address, c.buyer_city].filter(Boolean).join(", ")}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: C.muted, display: "flex", gap: 14, flexWrap: "wrap" }}>
        <span>Ref: <span style={{ color: C.text }}>{c.etransfer_reference || "not given"}</span></span>
        {c.etransfer_sent_on ? <span>Sent {c.etransfer_sent_on}</span> : null}
        <span>Claimed {when(c.created_at)}</span>
        {c.status === "verified" ? <span style={{ color: C.green }}>Verified {when(c.verified_at)}{c.verified_via ? ` via ${c.verified_via.replace(/_/g, " ")}` : ""}{c.verified_by && c.verified_via === "admin" ? ` by ${c.verified_by}` : ""}</span> : null}
        {c.status === "void" ? <span>Void {when(c.voided_at)}{c.void_reason ? ` — ${c.void_reason}` : ""}</span> : null}
        <span title={c.email_error || ""}>E-mail {c.email_sent_at ? "✓" : c.email_error ? "✗ failed" : "—"}</span>
        <span title={c.sms_error || ""}>Text {c.sms_sent_at ? "✓" : c.sms_error ? "✗ failed" : "—"}</span>
        <span>Jobber {c.jobber_note_id ? "note ✓" : c.jobber_client_id || c.native_client_id ? "client, note pending" : "—"}</span>
      </div>
      {c.status !== "void" && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {c.status === "pending" && (
            <Btn size="sm" kind="green" disabled={!!busy} onClick={() => onVerify(c)}>{busy === c.claim_id ? "…" : "Verify (paid)"}</Btn>
          )}
          <Btn size="sm" kind="danger" disabled={!!busy} onClick={() => onVoid(c)}>{busy === c.claim_id ? "…" : "Void"}</Btn>
        </div>
      )}
    </div>
  );
}

export default function RaffleAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`${API}?action=list`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j);
    } catch (e) {
      setError(e.message || "Could not load the raffle.");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (action, payload, key) => {
    setBusy(key || action);
    setError(null);
    setNotice(null);
    try {
      const r = await apiFetch(`${API}?action=${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload || {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      return j;
    } catch (e) {
      setError(e.message || `${action} failed`);
      return null;
    } finally {
      setBusy(null);
      load();
    }
  }, [load]);

  const onVerify = async (c) => {
    const ref = window.prompt(`Mark ${rangeText(c.tickets)} (${c.buyer_name}) as PAID.\n\nInterac reference number, if you have it (optional):`, c.etransfer_reference || "");
    if (ref === null) return;
    const j = await act("verify", { claim_id: c.claim_id, reference: ref }, c.claim_id);
    if (j) setNotice(`Verified ${rangeText(c.tickets)} for ${c.buyer_name}. The confirmation e-mail goes out on the next sweep (within 10 minutes).`);
  };
  // A voided number is not lost: it goes back on sale once the last number of
  // the run has sold (api/raffle.js re-sells void numbers last, lowest first).
  const onVoid = async (c) => {
    const last = data?.config?.last || "the last number";
    const reason = window.prompt(`VOID ${rangeText(c.tickets)} (${c.buyer_name})?\n\nThe number${c.tickets.length === 1 ? " goes" : "s go"} back on sale after ${last} is sold — never to anyone before that. Reason:`, "e-transfer never arrived");
    if (reason === null) return;
    const j = await act("void", { claim_id: c.claim_id, reason }, c.claim_id);
    if (j) setNotice(`Voided ${rangeText(c.tickets)}. ${c.tickets.length === 1 ? "It goes" : "They go"} back on sale after ${last} is sold.`);
  };
  const onReconcile = async () => {
    const j = await act("sweep");
    if (j) setNotice(`Sweep done — ${j.reconcile?.verified || 0} claim(s) verified from e-transfers on file, ${j.notes?.noted || 0} Jobber note(s) written, ${j.emails?.sent || 0} confirmation e-mail(s) sent.`);
  };
  const onTestEmail = async () => {
    const j = await act("email-test", { verified: false });
    if (j) setNotice(`Test ticket e-mail sent to ${j.to}. The text message would read: "${j.sms}"`);
  };
  // Test mode: the whole lane runs on fake numbers 900001+ at $1, open now,
  // TEST on every surface, no Jobber writes. The real run is never touched.
  const onToggleTest = async () => {
    const on = !data?.config?.testMode;
    if (on && !window.confirm("Switch TEST MODE on?\n\nThe public page will issue fake numbers from 900001 at $1 each, say TEST everywhere, and write nothing to Jobber. Real tickets are untouched. Switch it off (and purge) when you are done.")) return;
    const j = await act("test-mode", { on }, "test-mode");
    if (j) setNotice(j.testMode ? `TEST MODE is on — /raffle now issues ${j.config.first}+ at $${j.config.price}.` : "Test mode is off — /raffle is back on the real run.");
  };
  const onPurgeTest = async () => {
    if (!window.confirm("Delete every TEST ticket and the test counter, and release the $1 deposits they were verified from (marked dismissed on the Payments tab)?\n\nReal tickets cannot be touched by this.")) return;
    const j = await act("purge-test", {}, "purge-test");
    if (j) setNotice(`Purged ${j.ticketsDeleted} test ticket(s) in ${j.claims} claim(s); ${j.depositsReleased} deposit row(s) released.`);
  };

  const claims = useMemo(() => data?.claims || [], [data]);
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return claims.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!needle) return true;
      if (/^\d+$/.test(needle)) return c.tickets.some((t) => String(t).includes(needle));
      return [c.buyer_name, c.buyer_email, c.buyer_phone, c.etransfer_reference, c.buyer_address, c.buyer_city]
        .some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [claims, q, filter]);

  // The hospice's list — one line per ticket, built here from what is on screen
  // so the download needs no second request and carries the admin's session.
  const exportCsv = () => {
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = ["ticket,name,phone,email,address,city,amount,donation,reference,sent_on,status,verified_at,claimed_at"];
    // Live tickets first (the draw list), void claims after — a re-sold number
    // then reads as its live holder, with the cancelled claim further down.
    for (const c of [...claims].sort((a, b) => (a.status === "void") - (b.status === "void"))) {
      for (const t of c.tickets) {
        lines.push([t, c.buyer_name, c.buyer_phone, c.buyer_email, c.buyer_address, c.buyer_city,
          (Number(c.amount_cents) / 100).toFixed(2), (Number(c.donation_cents) / 100).toFixed(2), c.etransfer_reference,
          c.etransfer_sent_on, c.status, c.verified_at, c.created_at].map(esc).join(","));
      }
    }
    const blob = new Blob([lines.join("\r\n") + "\r\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gemstone-raffle-${data?.config?.key || "tickets"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const cfg = data?.config || {};
  const counter = data?.counter || {};
  const totals = data?.totals || {};
  const [stateLabel, stateColor] = STATE_LABEL[cfg.state] || ["—", C.muted];
  const orphans = data?.orphans || [];
  const possible = data?.possible || [];
  const testMode = !!cfg.testMode;
  const testRows = Number(data?.testRows || 0);
  const resellable = Array.isArray(counter.resellable) ? counter.resellable : [];

  return (
    <div style={{ padding: "16px 16px 40px", color: C.text, fontFamily: "'IBM Plex Sans',system-ui,sans-serif", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Raffle Tickets</h1>
          <div style={{ color: C.muted, fontSize: 13 }}>Gemstone lighting raffle for the Shuswap Hospice Society · draw Nov 12, 5 pm · tickets {cfg.first}–{cfg.last}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</Btn>
          <Btn onClick={onReconcile} disabled={!!busy} title="Re-match pending claims against the e-transfers on file, write Jobber notes, send confirmations">{busy === "sweep" ? "…" : "Reconcile now"}</Btn>
          <Btn onClick={exportCsv} disabled={!claims.length} kind="accent">Export CSV for the hospice</Btn>
          <Btn onClick={onTestEmail} disabled={!!busy} title="Sends the ticket e-mail to YOUR address only">{busy === "email-test" ? "…" : "Send me a test e-mail"}</Btn>
          <Btn onClick={onToggleTest} disabled={!!busy || !data} kind={testMode ? "danger" : "ghost"} title="Run the whole lane on fake numbers 900001+ at $1 so a real $1 e-transfer can prove the loop">{busy === "test-mode" ? "…" : testMode ? "Test mode: ON — switch off" : "Test mode: off"}</Btn>
          {(testMode || testRows > 0) && <Btn onClick={onPurgeTest} disabled={!!busy} kind="danger">{busy === "purge-test" ? "…" : `Purge ${testRows} test ticket${testRows === 1 ? "" : "s"}`}</Btn>}
        </div>
      </div>

      {testMode && (
        <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: C.redDim, border: "1px solid rgba(239,68,68,.4)", color: C.red, fontSize: 13, fontWeight: 700 }}>
          TEST MODE — /raffle is issuing fake numbers {cfg.first}–{cfg.last} at ${cfg.price} a ticket, open right now, nothing written to Jobber. Everything below is test data. Purge, then switch test mode off before Oct 20.
        </div>
      )}
      {!testMode && testRows > 0 && (
        <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: C.yellowDim, border: "1px solid rgba(234,179,8,.3)", color: C.yellow, fontSize: 13 }}>
          {testRows} test ticket{testRows === 1 ? "" : "s"} still stored from a test run — press Purge to clear them (they are not in the real run and not in the CSV).
        </div>
      )}

      {/* Switch + licence state — the two things that must be right before Oct 20 */}
      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", fontSize: 13 }}>
        {testMode ? <Badge label="TEST MODE" color={C.red} bg={C.redDim} /> : <Badge label={stateLabel} color={stateColor} bg={`${stateColor}22`} />}
        <span style={{ color: cfg.licence ? C.text : C.red }}>
          {cfg.licence ? `Licence #${cfg.licence}` : "Licence # NOT SET — set RAFFLE_LICENCE_NO in Vercel; tickets say \"pending\" until then"}
        </span>
        <span style={{ color: C.muted }}>e-transfers to {cfg.etransferEmail}</span>
        <span style={{ color: C.muted }}>texts {cfg.smsOn ? "on" : "off"}</span>
        <span style={{ color: C.muted }}>sales {cfg.salesStart} → {String(cfg.salesEnd || "").replace("T", " ")} Pacific</span>
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Tile label="Issued" value={counter.issued ?? "—"} sub={`of ${cfg.count || 100}`} />
        <Tile label="Left" value={counter.remaining ?? "—"} sub={resellable.length ? `incl. ${resellable.length} voided, sold last` : undefined} color={counter.remaining === 0 ? C.red : C.accent} />
        <Tile label="Verified" value={totals.ticketsVerified ?? 0} sub={money(totals.amountVerifiedCents)} color={C.green} />
        <Tile label="Pending" value={totals.ticketsPending ?? 0} sub="awaiting e-transfer" color={C.yellow} />
        <Tile label="Void" value={totals.ticketsVoid ?? 0} sub={resellable.length ? `${rangeText(resellable)} back on sale after ${cfg.last}` : undefined} color={C.muted} />
        <Tile label="Donations" value={money(totals.donationVerifiedCents)} sub="verified, above the ticket price" />
      </div>

      {error && <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: C.redDim, border: "1px solid rgba(239,68,68,.3)", color: C.red, fontSize: 13 }}>{error}</div>}
      {notice && <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: C.greenDim, border: "1px solid rgba(34,197,94,.3)", color: C.green, fontSize: 13 }}>{notice}</div>}

      {orphans.length > 0 && (
        <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: C.yellowDim, border: "1px solid rgba(234,179,8,.3)", fontSize: 13 }}>
          <strong style={{ color: C.yellow }}>{orphans.length} raffle e-transfer{orphans.length === 1 ? "" : "s"} with no claim yet</strong> — the memo said raffle/Gemstone but nobody has filled the form. Find them on the Payments tab or call the sender:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {orphans.map((o) => <li key={o.id}>{money(Math.round(Number(o.amount) * 100))} from {o.sender_name} · ref {o.reference_number || "—"} · {when(o.received_at)}{o.message_text ? ` · "${o.message_text}"` : ""}</li>)}
          </ul>
        </div>
      )}

      {possible.length > 0 && (
        <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: C.blueDim, border: "1px solid rgba(59,130,246,.3)", fontSize: 13 }}>
          <strong style={{ color: C.blue }}>{possible.length} e-transfer{possible.length === 1 ? "" : "s"} that may be a raffle ticket</strong> — same amount as a pending claim, but sent under a different name (a company account, a spouse). If it's theirs: press <em>Verify (paid)</em> on the claim and type the Interac reference shown here; that links the deposit and takes it out of the Payments queue.
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {possible.map((o) => <li key={o.id}>{money(Math.round(Number(o.amount) * 100))} from <strong>{o.sender_name}</strong> · ref <code style={{ userSelect: "all" }}>{o.reference_number || "—"}</code> · {when(o.received_at)}{o.message_text ? ` · "${o.message_text}"` : ""} → pending: {(o.claims || []).map((c) => `${c.buyer_name} (${rangeText(c.tickets)})`).join(", ")}</li>)}
          </ul>
        </div>
      )}

      {/* Search — draw day: type the number drawn */}
      <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticket number, name, phone, e-mail or reference…"
          style={{ flex: "1 1 260px", padding: "10px 12px", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 15, fontFamily: "inherit", outline: "none" }}
        />
        {[["all", "All"], ["pending", "Pending"], ["verified", "Verified"], ["void", "Void"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} style={{
            padding: "7px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            background: filter === k ? C.accentDim : "transparent", color: filter === k ? C.accent : C.muted, border: `1px solid ${filter === k ? C.accent : C.border}`,
          }}>{label}{k !== "all" && totals[k] != null ? ` (${totals[k]})` : ""}</button>
        ))}
      </div>

      <div style={{ marginTop: 12 }}>
        {loading && !data ? <div style={{ color: C.muted, fontSize: 14 }}>Loading…</div>
          : !claims.length ? <div style={{ color: C.muted, fontSize: 14 }}>No tickets claimed yet. The public form is at <a href="/raffle" target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>/raffle</a>.</div>
          : !visible.length ? <div style={{ color: C.muted, fontSize: 14 }}>Nothing matches “{q}”.</div>
          : visible.map((c) => <ClaimCard key={c.claim_id} c={c} busy={busy} onVerify={onVerify} onVoid={onVoid} />)}
      </div>

      <div style={{ marginTop: 20, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
        Numbers are issued in order the moment a buyer submits. A voided claim&rsquo;s numbers go back on sale only after the last number
        of the run ({cfg.last}) has sold, lowest first; the void claim stays here as the record. Verification comes from the Interac
        deposit e-mails (reference, memo saying raffle/Gemstone, or exact amount + sender name) every 30 minutes; “Verify (paid)” is for
        a payment that arrived some other way. Each claim gets one client note and the tag “Gemstone Raffle 2026” in Jobber once the
        client has been pushed there.
      </div>
    </div>
  );
}
